import React, { useState, useEffect } from "react";
import {
  FaUser,
  FaEnvelope,
  FaPhone,
  FaUserPlus,
  FaSearch,
} from "react-icons/fa";
import { CiEdit } from "react-icons/ci";
import {
  MdDeleteOutline,
  MdOutlineAssignment,
  MdOutlineFolder,
} from "react-icons/md";
import { BsListTask } from "react-icons/bs";
import { FaProjectDiagram } from "react-icons/fa";
import { GoProjectSymlink } from "react-icons/go";
import DeleteUser from "../pages/DeleteUser"; // Assume DeleteUser component exists
import AssignedTasksPopover from "../pages/AssignedTasksPopover"; // Assume this exists
import { IconWrapper } from "./IconWrapper";
import { db } from "../firebase/firebase";
import { doc, getDoc, collection, getDocs } from "firebase/firestore";
import { getThemeClasses } from "../utils/themeUtils";
import CustomDropdown from "./CustomDropdown";
import { useColorMode, Skeleton } from "@chakra-ui/react";

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: string;
  employeeCode?: string;
  department?: string;
  address1?: string;
  address2?: string;
  organizationId: string;
  permissions?: "Admin" | "Editor" | "Viewer";
}

interface UserTableProps {
  users: User[];
  isAdmin: boolean;
  canAssignProjects?: boolean;
  cardBg: string;
  textColor: string;
  handleOpenAssignProjects: (user: User) => void;
  setEditingUser: (user: any) => void;
  setNewUser: (user: any) => void;
  onUserModalOpen: () => void;
  handleDeleteUser: (id: string, reason: string) => void;
  isLoading: boolean;
  organizationId: string;
}

const UserTable: React.FC<UserTableProps> = ({
  users,
  isAdmin,
  canAssignProjects = false,
  cardBg,
  textColor,
  handleOpenAssignProjects,
  setEditingUser,
  setNewUser,
  onUserModalOpen,
  handleDeleteUser,
  isLoading,
  organizationId,
}) => {
  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";
  const theme = getThemeClasses(isDark);

  const renderSkeletonRows = () => {
    return Array.from({ length: 6 }).map((_, index) => (
      <tr key={index} className={`${isDark ? 'border-b border-[#2E2F33]' : 'border-b border-gray-200'}`}>
        <td className="px-2 py-6"><Skeleton h="16px" w="20px" borderRadius="sm" startColor={isDark ? "gray.700" : "gray.200"} endColor={isDark ? "gray.600" : "gray.300"} /></td>
        <td className="px-2 py-6"><Skeleton h="16px" w="110px" borderRadius="sm" startColor={isDark ? "gray.700" : "gray.200"} endColor={isDark ? "gray.600" : "gray.300"} /></td>
        <td className="px-2 py-6"><Skeleton h="16px" w="150px" borderRadius="sm" startColor={isDark ? "gray.700" : "gray.200"} endColor={isDark ? "gray.600" : "gray.300"} /></td>
        <td className="px-2 py-6"><Skeleton h="16px" w="100px" borderRadius="sm" startColor={isDark ? "gray.700" : "gray.200"} endColor={isDark ? "gray.600" : "gray.300"} /></td>
        <td className="px-2 py-6"><Skeleton h="16px" w="70px" borderRadius="sm" startColor={isDark ? "gray.700" : "gray.200"} endColor={isDark ? "gray.600" : "gray.300"} /></td>
        <td className="px-2 py-6"><Skeleton h="16px" w="40px" m="auto" borderRadius="sm" startColor={isDark ? "gray.700" : "gray.200"} endColor={isDark ? "gray.600" : "gray.300"} /></td>
        <td className="px-2 py-6"><Skeleton h="16px" w="40px" m="auto" borderRadius="sm" startColor={isDark ? "gray.700" : "gray.200"} endColor={isDark ? "gray.600" : "gray.300"} /></td>
        {isAdmin && <td className="px-2 py-3"><Skeleton h="14px" w="50px" m="auto" borderRadius="sm" startColor={isDark ? "gray.700" : "gray.200"} endColor={isDark ? "gray.600" : "gray.300"} /></td>}
      </tr>
    ));
  };

  const [searchQuery, setSearchQuery] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState<string | null>(null);
  const [deleteReason, setDeleteReason] = useState<string>("");
  const [selectedReasonType, setSelectedReasonType] = useState<string>("");
  const [showAudit, setShowAudit] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditEntries, setAuditEntries] = useState<any[]>([]);
  const [departments, setDepartments] = useState<{ id: string; name: string }[]>([]);
  const [selectedDepartment, setSelectedDepartment] = useState<string>("");

  // Fetch departments
  useEffect(() => {
    const fetchDepartments = async () => {
      if (!organizationId) return;
      try {
        const q = collection(db, "organization", organizationId, "departments");
        const snapshot = await getDocs(q);
        const deptList = snapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().name as string
        }));
        setDepartments(deptList);
      } catch (error) {
        console.error("Error fetching departments", error);
      }
    };
    fetchDepartments();
  }, [organizationId]);

  // Filter and sort users
  const filteredUsers = users.filter((user) => {
    const searchLower = searchQuery.toLowerCase();
    const matchesSearch = (
      user.firstName.toLowerCase().includes(searchLower) ||
      user.lastName.toLowerCase().includes(searchLower) ||
      user.email.toLowerCase().includes(searchLower) ||
      (user.role && user.role.toLowerCase().includes(searchLower))
    );
    const matchesDept = selectedDepartment ? user.department === selectedDepartment : true;

    return matchesSearch && matchesDept;
  }).sort((a, b) => {
    const deptA = a.department || "Other";
    const deptB = b.department || "Other";
    if (deptA < deptB) return -1;
    if (deptA > deptB) return 1;
    return a.firstName.localeCompare(b.firstName);
  });

  const handleDeleteClick = (userId: string) => {
    setUserToDelete(userId);
    setShowDeleteConfirm(true);
    setDeleteReason("");
    setSelectedReasonType("");
  };

  const confirmDelete = () => {
    if (userToDelete) {
      let reason = deleteReason.trim();

      // If a predefined reason is selected, use that
      if (selectedReasonType && selectedReasonType !== "others") {
        reason = selectedReasonType;
      }

      if (!reason) return; // safety guard
      handleDeleteUser(userToDelete, reason);
      setShowDeleteConfirm(false);
      setUserToDelete(null);
      setDeleteReason("");
      setSelectedReasonType("");
    }
  };

  const openAuditUsers = async () => {
    setShowAudit(true);
    setAuditLoading(true);
    try {
      const auditDocRef = doc(
        db,
        "organization",
        organizationId,
        "audit",
        "deletions"
      );
      const snap = await getDoc(auditDocRef);
      const data = snap.exists() ? (snap.data() as any) : {};
      const entries = Array.isArray(data.entries) ? data.entries : [];
      setAuditEntries(entries);
    } catch (e) {
      setAuditEntries([]);
    } finally {
      setAuditLoading(false);
    }
  };

  const closeAuditUsers = () => {
    setShowAudit(false);
    setSearchQuery("");
  };

  const filteredAudit = auditEntries.filter((e) => {
    const searchLower = searchQuery.toLowerCase();
    const name = `${e.firstName || ""} ${e.lastName || ""}`.toLowerCase();
    return (
      name.includes(searchLower) ||
      (e.email || "").toLowerCase().includes(searchLower) ||
      (e.role || "").toLowerCase().includes(searchLower) ||
      (e.reason || "").toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Main Heading Section - Dynamic Theme */}
      <div className={`flex-shrink-0 flex justify-between items-center p-4 ${theme.headerGradient} rounded-md mb-4 border ${theme.border}`}>
        {isDark && (
          <>
            <span className={theme.headerOverlayGlow} />
            <span className={theme.headerOverlayTopLine} />
            <span className={theme.headerOverlayBottomGlow} />
            <span className={theme.headerOverlaySweep} />
          </>
        )}
        <div className="flex flex-col space-y-1">
          <span className={`text-2xl font-bold ${theme.text}`}>
            {showAudit ? "User Audit" : "User management"}
          </span>
          <span className={`text-xs ${theme.textMuted}`}>
            {showAudit
              ? "Review users removed from the organization and their removal reasons."
              : "Start by Adding a User to Empower Enterprise Oversight"}
          </span>
        </div>

        {/* Add User Button on the right of the main heading - Dynamic Theme */}
        {isAdmin && (
          <div className="flex items-center gap-2">
            {!showAudit && (
              <button
                onClick={onUserModalOpen}
                className={`flex items-center px-3 py-1.5 ${theme.primaryButton} rounded-full text-sm focus:outline-none`}
              >
                <IconWrapper
                  icon={FaUserPlus}
                  className="mr-1"
                  size={16}
                />
                Add User
              </button>
            )}
            {showAudit && (
              <div className="flex justify-end ">
                <button
                  onClick={closeAuditUsers}
                  className={`px-3 py-1.5 text-sm font-medium ${theme.text} ${isDark ? theme.card : 'bg-gray-100'} border border-[#B59143] rounded-full hover:${isDark ? 'bg-[#3A4553]' : 'bg-gray-200'} focus:outline-none focus:ring-2 focus:ring-[#B59143] focus:ring-opacity-50 transition-all duration-200`}
                >
                  Back to Members
                </button>
              </div>
            )}
            <button
              onClick={openAuditUsers}
              className={`flex items-center px-3 py-1.5 ${isDark ? theme.goldOutlineButton : theme.goldOutlineButton} rounded-full text-sm focus:outline-none`}
            >
              Removed Users
            </button>
          </div>
        )}
      </div>

      {/* Search Bar and Status Section */}
      <div className={`w-full rounded-md border ${theme.border} ${theme.card}`}>
        <div className={`flex justify-between items-center px-4 py-3 border-b ${theme.border} rounded-t-md flex-wrap`}>
          <div className="flex items-center flex-1">
            <div className={`relative flex items-center max-w-sm w-full rounded-full ${theme.cardMuted} overflow-hidden`}>
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <IconWrapper
                  icon={FaSearch}
                  className={`h-4 w-4 ${theme.textMuted}`}
                  size={16}
                />
              </div>
              <input
                type="text"
                placeholder={
                  showAudit ? "Search deleted users..." : "Search users..."
                }
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                }}
                className={`${theme.searchInput} block w-full pl-10 pr-3 py-1.5 border rounded-full leading-5 focus:outline-none sm:text-sm`}
              />
            </div>

            <div className="ml-4 w-48">
              <CustomDropdown
                options={[{ value: "", label: "All Departments" }, ...departments.map(d => ({ value: d.name, label: d.name }))]}
                value={selectedDepartment}
                onChange={(val) => {
                  setSelectedDepartment(val);
                }}
                isDark={isDark}
                placeholder="All Departments"
                className={`${theme.searchInput} w-full rounded-full border px-3 py-1.5 text-sm`}
              />
            </div>
          </div>

          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold mt-4 md:mt-0 ${isDark ? 'text-[#C3C9D3] border border-[#4A4D54]' : 'text-gray-600 border border-gray-300'}`}>
            {showAudit
              ? `${filteredAudit.length} deleted`
              : `${filteredUsers.length} members`}
          </span>
        </div>

        <div className="p-4">
          {/* Table Container - Scrollable body */}
          <div className={`flex flex-col flex-1 min-h-0 w-full rounded-md ${isDark ? 'bg-[#1A1C1F] border border-[#2E2F33] shadow-[0_6px_24px_rgba(0,0,0,0.35)]' : 'bg-white border border-gray-200 shadow-sm'}`}>
            {/* Spinner overlay removed in favor of inline skeleton loader */}

            <div className="flex-1 overflow-auto" style={{ maxHeight: "68vh" }}>
              {showAudit ? (
                filteredAudit.length === 0 ? (
                  <div className={`text-center py-8 col-span-full text-lg font-medium ${isDark ? 'text-[#C3C9D3]' : 'text-gray-500'}`}>
                    No users found.
                  </div>
                ) : (
                  <table className={`min-w-full divide-y ${isDark ? 'divide-[#2E2F33]' : 'divide-gray-200'}`}>
                    <thead className={`sticky top-0 z-10 ${isDark ? 'bg-[#14161B]' : 'bg-gray-50'}`}>
                      <tr>
                        <th className={`px-2 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'bg-[#14161B] text-white' : 'bg-gray-50 text-gray-600'}`}>
                          S.No
                        </th>
                        <th className={`px-2 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'bg-[#14161B] text-white' : 'bg-gray-50 text-gray-600'}`}>
                          Name
                        </th>
                        <th className={`px-2 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'bg-[#14161B] text-white' : 'bg-gray-50 text-gray-600'}`}>
                          Email
                        </th>
                        <th className={`px-2 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'bg-[#14161B] text-white' : 'bg-gray-50 text-gray-600'}`}>
                          Phone
                        </th>
                        <th className={`px-2 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'bg-[#14161B] text-white' : 'bg-gray-50 text-gray-600'}`}>
                          Role
                        </th>
                        <th className={`px-2 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'bg-[#14161B] text-white' : 'bg-gray-50 text-gray-600'}`}>
                          Reason
                        </th>
                      </tr>
                    </thead>
                    <tbody className={`${isDark ? 'bg-[#1A1C1F]' : 'bg-white'} divide-y ${isDark ? 'divide-[#2E2F33]' : 'divide-gray-200'}`}>
                      {auditLoading ? (
                        Array.from({ length: 5 }).map((_, index) => (
                          <tr key={index} className={`${isDark ? 'border-b border-[#2E2F33]' : 'border-b border-gray-200'}`}>
                            <td className="px-2 py-3"><Skeleton h="14px" w="20px" borderRadius="sm" startColor={isDark ? "gray.700" : "gray.200"} endColor={isDark ? "gray.600" : "gray.300"} /></td>
                            <td className="px-2 py-3"><Skeleton h="14px" w="110px" borderRadius="sm" startColor={isDark ? "gray.700" : "gray.200"} endColor={isDark ? "gray.600" : "gray.300"} /></td>
                            <td className="px-2 py-3"><Skeleton h="14px" w="150px" borderRadius="sm" startColor={isDark ? "gray.700" : "gray.200"} endColor={isDark ? "gray.600" : "gray.300"} /></td>
                            <td className="px-2 py-3"><Skeleton h="14px" w="100px" borderRadius="sm" startColor={isDark ? "gray.700" : "gray.200"} endColor={isDark ? "gray.600" : "gray.300"} /></td>
                            <td className="px-2 py-3"><Skeleton h="14px" w="70px" borderRadius="sm" startColor={isDark ? "gray.700" : "gray.200"} endColor={isDark ? "gray.600" : "gray.300"} /></td>
                            <td className="px-2 py-3"><Skeleton h="14px" w="120px" borderRadius="sm" startColor={isDark ? "gray.700" : "gray.200"} endColor={isDark ? "gray.600" : "gray.300"} /></td>
                          </tr>
                        ))
                      ) : (
                        filteredAudit.map((e, index) => (
                          <tr key={`${e.userId}-${index}`} className={`${isDark ? 'hover:bg-[#2A3441]' : 'hover:bg-[#F2F4F7]'}`}>
                            <td className={`px-2 py-2 whitespace-nowrap text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              {index + 1}
                            </td>
                            <td className={`px-2 py-2 whitespace-nowrap text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              <div className="flex items-center">
                                <IconWrapper
                                  icon={FaUser}
                                  className="text-gray-400 mr-2"
                                  size={12}
                                />
                                <span>
                                  {`${e.firstName || ""} ${e.lastName || ""
                                    }`.trim() || "N/A"}
                                </span>
                              </div>
                            </td>
                            <td className={`px-2 py-2 whitespace-nowrap text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              {e.email || "N/A"}
                            </td>
                            <td className={`px-2 py-2 whitespace-nowrap text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              {e.phone || "N/A"}
                            </td>
                            <td className={`px-2 py-2 whitespace-nowrap text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${isDark ? 'border border-[#B59143] text-[#FBD282]' : 'bg-[#FFF7E6] text-[#B59143] border border-[#E8C16C]'}`}>
                                {e.role || "N/A"}
                              </span>
                            </td>
                            <td className={`px-2 py-2 whitespace-normal text-sm max-w-[300px] ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              {e.reason || "-"}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                )
              ) : !isLoading && filteredUsers.length === 0 ? (
                <div className={`text-center py-8 col-span-full text-lg font-medium ${isDark ? 'text-[#C3C9D3]' : 'text-gray-500'}`}>
                  No users found.
                </div>
              ) : (
                <table className={`min-w-full divide-y ${isDark ? 'divide-[#2E2F33]' : 'divide-gray-200'}`}>
                  <thead className={`sticky top-0 z-10 ${isDark ? 'bg-[#14161B]' : 'bg-gray-50'}`}>
                    <tr>
                      <th
                        scope="col"
                        className={`px-2 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'bg-[#14161B] text-white' : 'bg-gray-50 text-gray-600'}`}
                      >
                        S.No
                      </th>
                      <th
                        scope="col"
                        className={`px-2 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'bg-[#14161B] text-white' : 'bg-gray-50 text-gray-600'}`}
                      >
                        Name
                      </th>
                      <th
                        scope="col"
                        className={`px-2 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'bg-[#14161B] text-white' : 'bg-gray-50 text-gray-600'}`}
                      >
                        Email
                      </th>
                      <th
                        scope="col"
                        className={`px-2 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'bg-[#14161B] text-white' : 'bg-gray-50 text-gray-600'}`}
                      >
                        Phone
                      </th>
                      <th
                        scope="col"
                        className={`px-2 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'bg-[#14161B] text-white' : 'bg-gray-50 text-gray-600'}`}
                      >
                        Role
                      </th>
                      <th
                        scope="col"
                        className={`px-2 py-3 text-center text-xs font-semibold uppercase tracking-wider ${isDark ? 'bg-[#14161B] text-white' : 'bg-gray-50 text-gray-600'}`}
                      >
                        Tasks
                      </th>
                      <th
                        scope="col"
                        className={`px-2 py-3 text-center text-xs font-semibold uppercase tracking-wider ${isDark ? 'bg-[#14161B] text-white' : 'bg-gray-50 text-gray-600'}`}
                      >
                        Projects
                      </th>
                      {isAdmin && (
                        <th
                          scope="col"
                          className={`px-2 py-3 text-center text-xs font-semibold uppercase tracking-wider ${isDark ? 'bg-[#14161B] text-white' : 'bg-gray-50 text-gray-600'}`}
                        >
                          Actions
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className={`${isDark ? 'bg-[#1A1C1F]' : 'bg-white'} divide-y ${isDark ? 'divide-[#2E2F33]' : 'divide-gray-200'}`}>
                    {isLoading ? (
                      renderSkeletonRows()
                    ) : (
                      filteredUsers.map((user, index) => {
                        const currentDept = user.department || "Other";
                        const prevDept = index > 0 ? filteredUsers[index - 1].department || "Other" : null;
                        const showHeader = index === 0 || currentDept !== prevDept;

                        return (
                          <React.Fragment key={user.id}>
                            {showHeader && (
                              <tr className={`${isDark ? 'bg-[#2A3441]' : 'bg-gray-100 text-gray-800'}`}>
                                <td colSpan={isAdmin ? 8 : 7} className="px-4 py-2">
                                  <div className="flex items-center justify-between">
                                    <span className={`text-sm font-bold ${isDark ? 'text-[#FFFFFF]' : 'text-gray-800'}`}>{currentDept}</span>
                                    <span className={`inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-xs font-normal border ${isDark ? 'bg-transparent border-gray-600 text-gray-400' : 'bg-gray-50 border-gray-200 text-gray-500'}`}>
                                      {filteredUsers.filter(u => (u.department || "Other") === currentDept).length} Employees
                                    </span>
                                  </div>
                                </td>
                              </tr>
                            )}
                            <tr className={`${isDark ? 'hover:bg-[#2A3441]' : 'hover:bg-[#F2F4F7]'}`}>
                              <td className={`px-2 py-2 whitespace-nowrap text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                {index + 1}
                              </td>
                              <td className={`px-2 py-2 whitespace-nowrap text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                <div className="flex items-center">
                                  <IconWrapper
                                    icon={FaUser}
                                    className="text-gray-400 mr-2"
                                    size={12}
                                  />
                                  <span>{`${user.firstName} ${user.lastName}`}</span>
                                </div>
                              </td>
                              <td className={`px-2 py-2 whitespace-nowrap text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                <div className="flex items-center">
                                  <IconWrapper
                                    icon={FaEnvelope}
                                    className="text-gray-400 mr-2"
                                    size={12}
                                  />
                                  <span>{user.email}</span>
                                </div>
                              </td>
                              <td className={`px-2 py-2 whitespace-nowrap text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                <div className="flex items-center">
                                  <IconWrapper
                                    icon={FaPhone}
                                    className="text-gray-400 mr-2"
                                    size={12}
                                  />
                                  <span>{user.phone || "N/A"}</span>
                                </div>
                              </td>
                              <td className={`px-2 py-2 whitespace-nowrap text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${isDark ? 'border border-[#B59143] text-[#FBD282]' : 'bg-[#FFF7E6] text-[#B59143] border border-[#E8C16C]'}`}>
                                  {user.role}
                                </span>
                              </td>
                              <td className={`px-2 py-2 whitespace-nowrap text-sm text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                <span className={`${isDark ? 'text-[#B59143]' : 'text-[#B59143]'}`}>
                                  <AssignedTasksPopover
                                    color={textColor}
                                    bg={cardBg}
                                    user={user}
                                  />
                                </span>
                              </td>
                              <td className={`px-2 py-2 whitespace-nowrap text-sm text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                <button
                                  onClick={() => handleOpenAssignProjects(user)}
                                  className={`p-1 rounded-full ${isDark ? 'text-[#B59143] hover:bg-[#2A3441] focus:ring-[#B59143]' : 'text-[#B59143] hover:bg-[#FFF7E6] focus:ring-[#B59143]'} focus:outline-none focus:ring-2 focus:ring-opacity-50`}
                                  aria-label={`Assign projects to ${user.firstName} ${user.lastName}`}
                                >
                                  <IconWrapper icon={GoProjectSymlink} size={16} />
                                </button>
                              </td>
                              {isAdmin && (
                                <td className="px-2 py-2 whitespace-nowrap text-sm text-center font-medium">
                                  <button
                                    onClick={() => {
                                      setNewUser(user);
                                      setEditingUser(user);
                                      onUserModalOpen();
                                    }}
                                    className={`inline-flex items-center p-1 rounded-full mr-1 ${isDark ? 'text-[#B59143] hover:bg-[#2A3441] focus:ring-[#B59143]' : 'text-[#B59143] hover:bg-[#FFF7E6] focus:ring-[#B59143]'} focus:outline-none focus:ring-2 focus:ring-opacity-50`}
                                  >
                                    <IconWrapper icon={CiEdit} size={16} />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteClick(user.id)}
                                    className={`inline-flex items-center p-1 rounded-full ${isDark ? 'text-[#B59143] hover:bg-[#2A3441] focus:ring-[#B59143]' : 'text-[#B59143] hover:bg-[#FFF7E6] focus:ring-[#B59143]'} focus:outline-none focus:ring-2 focus:ring-opacity-50`}
                                  >
                                    <IconWrapper icon={MdDeleteOutline} size={16} />
                                  </button>
                                </td>
                              )}
                            </tr>
                          </React.Fragment>
                        );
                      })
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className={`${isDark ? 'bg-[#0B0B0F]/75' : 'bg-black/60'} backdrop-blur-[3px] absolute inset-0`} />
          <div className={`relative rounded-xl p-6 max-w-md w-full mx-4 border ${isDark ? 'bg-[#1A1C1F] border-[#2E2F33]' : 'bg-white border-gray-100'}`}>
            <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Confirm Deletion
            </h3>
            <p className={`${isDark ? 'text-[#C3C9D3]' : 'text-gray-600'} mb-4`}>
              Are you sure you want to delete this user? This action cannot be undone.
            </p>
            <div className="mb-5">
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-white' : 'text-gray-700'}`}>
                Reason (required)
              </label>
              <div className="mb-3">
                <select
                  value={selectedReasonType}
                  onChange={(e) => {
                    setSelectedReasonType(e.target.value);
                    if (e.target.value !== 'others') setDeleteReason('');
                  }}
                  className={`w-full rounded-md p-2 border focus:outline-none focus:ring-2 focus:ring-[#B59143] focus:border-[#B59143] ${isDark ? 'bg-[#0B0B0F] text-white border-[#2E2F33]' : 'bg-white text-gray-900 border-gray-300'}`}
                >
                  <option value="">Select a reason</option>
                  <option value="Resigned from the organization">Resigned from the organization</option>
                  <option value="Contract ended">Contract ended</option>
                  <option value="Performance issues">Performance issues</option>
                  <option value="Position eliminated">Position eliminated</option>
                  <option value="others">Others</option>
                </select>
              </div>

              {selectedReasonType === 'others' && (
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-white' : 'text-gray-700'}`}>
                    Specify reason
                  </label>
                  <textarea
                    value={deleteReason}
                    onChange={(e) => setDeleteReason(e.target.value)}
                    placeholder="Enter reason for removing this user"
                    className={`w-full min-h-[80px] rounded-md p-2 border focus:outline-none focus:ring-2 focus:ring-[#B59143] focus:border-[#B59143] ${isDark ? 'bg-[#0B0B0F] text-white border-[#2E2F33]' : 'bg-white text-gray-900 border-gray-300'}`}
                  />
                </div>
              )}

              {!selectedReasonType && (
                <span className="text-xs text-red-500">Please select a reason to proceed.</span>
              )}
              {selectedReasonType === 'others' && !deleteReason.trim() && (
                <span className="text-xs text-red-500">Please specify the reason to proceed.</span>
              )}
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setUserToDelete(null);
                  setDeleteReason('');
                  setSelectedReasonType('');
                }}
                className={`px-4 py-2 text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 ${isDark ? 'text-white bg-[#2A3441] focus:ring-[#B59143]' : 'text-gray-700 bg-gray-100 focus:ring-gray-500'} hover:${isDark ? 'bg-[#3A4553]' : 'bg-gray-200'}`}
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={!selectedReasonType || (selectedReasonType === 'others' && !deleteReason.trim())}
                className={`px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 ${(selectedReasonType && (selectedReasonType !== 'others' || deleteReason.trim()))
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-red-300 cursor-not-allowed'
                  }`}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toggle back to Members when in Audit mode */}
      {/* {showAudit && (
        <div className="flex justify-end mt-3">
          <button
            onClick={closeAuditUsers}
            className="px-3 py-1.5 text-sm font-medium text-purple-700 bg-white border border-purple-600 rounded-full hover:bg-purple-50 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-opacity-50"
          >
            Back to Members
          </button>
        </div>
      )} */}
    </div>
  );
};

export default UserTable;

